import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RewardStatusBadge } from "./RewardStatusBadge";
import { formatRewardAmount } from "@/lib/textUtils";
import { RewardData } from "@/components/referrals/tableConfigs";

interface RedeemModalProps {
  isOpen: boolean;
  onClose: () => void;
  reward: RewardData | null;
  onRedeem?: (rewardId: string) => void;
}

const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

export function RedeemModal({ isOpen, onClose, reward, onRedeem }: RedeemModalProps) {
  if (!reward) return null;

  const handleRedeem = () => {
    if (onRedeem) {
      onRedeem(reward.id);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-bold text-gray-900 space-grotesk">
            Redeem Reward
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 space-grotesk">
            Review and confirm your reward redemption details below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Reward Summary Card */}
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="space-y-4">
                {/* Candidate Info */}
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={reward.avatar} />
                    <AvatarFallback className="bg-gray-200 text-gray-700 text-sm font-medium">
                      {getInitials(reward.candidateName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-gray-900 space-grotesk">
                      {reward.candidateName}
                    </p>
                    <p className="text-sm text-gray-500 space-grotesk">
                      {reward.jobTitle}
                    </p>
                  </div>
                </div>

                {/* Reward Details */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide space-grotesk mb-1">
                      Reward Amount
                    </p>
                    <p className="text-2xl font-bold text-gray-900 space-grotesk">
                      R{formatRewardAmount(reward.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide space-grotesk mb-1">
                      Status
                    </p>
                    <RewardStatusBadge status={reward.status} />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide space-grotesk mb-1">
                    Date Earned
                  </p>
                  <p className="text-sm text-gray-900 space-grotesk">
                    {reward.date}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Redeem Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 space-grotesk mb-2">
              Redeem Information
            </h4>
            <p className="text-sm text-blue-800 space-grotesk">
                Once you redeem this reward through the YoYo Rewards app, the amount will be processed and transferred to your account.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleRedeem}
              disabled={reward.status === 'paid'}
              className="flex-1"
            >
              {reward.status === 'paid' ? 'Already Redeemed' : 'Confirm Redeem'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
